import {
  column,
  Css,
  dateColumn,
  defaultPage,
  GridDataRow,
  GridTable,
  IconButton,
  numericColumn,
  Pagination,
  Palette,
  simpleHeader,
} from "@homebound/beam";
import { useState } from "react";
import { priceCell, tagCell } from "src/components";
import { getTagTypeFromStatus } from "src/components/bid-contracts/BidContractHeader";
import { BidItemSuperDrawer_BidItemFragment } from "src/routes/libraries/item-catalog/components/BidItemSuperDrawer.generated";
import {
  BidItemCostHistory_BidContractLineItemsFragment,
  useBidItemCostHistoryQuery,
} from "src/routes/libraries/item-catalog/components/tabs/BidItemCostHistoryTab.generated";
import { createDevelopmentContractOverviewUrl, createProjectBidContractOverviewUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";

export type BidItemCostHistoryTabProps = {
  bidItem: BidItemSuperDrawer_BidItemFragment;
};

export function BidItemCostHistoryTab({ bidItem }: BidItemCostHistoryTabProps) {
  const [page, setPage] = useState(defaultPage);
  const query = useBidItemCostHistoryQuery({
    variables: { filter: { bidItemIds: [bidItem.id] }, page },
  });

  return (
    <div>
      <div css={Css.df.jcsb.$}>
        <div data-testid="bidItemName" css={Css.xl.mb2.$}>
          {bidItem.name}
        </div>
      </div>
      {queryResult(query, {
        data: (data) => {
          const bclis = data.bidContractLineItemsPage.entities;
          const totalCount = data.bidContractLineItemsPage.pageInfo.totalCount;
          return (
            <>
              <GridTable columns={createColumns()} rows={createRows(bclis)} />
              <Pagination page={[page, setPage]} totalCount={totalCount} />
            </>
          );
        },
      })}
    </div>
  );
}

type Row =
  | { kind: "header"; data: undefined }
  | { kind: "lineItem"; id: string; data: BidItemCostHistory_BidContractLineItemsFragment };

function createRows(bclis: BidItemCostHistory_BidContractLineItemsFragment[]): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...bclis.map((bcli) => ({
      kind: "lineItem" as const,
      id: bcli.id,
      data: bcli,
    })),
  ];
}

function createColumns() {
  return [
    column<Row>({
      header: "Trade Partner Contract",
      lineItem: (bcli) => (
        <div css={Css.df.$}>
          <div css={Css.df.aic.blue800.$}>{bcli.revision.bidContract.displayName}</div>
          <IconButton
            color={Palette.Blue800}
            inc={2}
            onClick={
              bcli.revision.bidContract.developments.length
                ? createDevelopmentContractOverviewUrl(bcli.revision.bidContract.developments[0].id, bcli.revision.id)
                : createProjectBidContractOverviewUrl(bcli.revision.bidContract.projects[0].id, bcli.revision.id)
            }
            openInNew
            icon="linkExternal"
          />
        </div>
      ),
      w: 1.5,
    }),
    column<Row>({
      header: "Developments/Communities",
      lineItem: (bcli) =>
        bcli.revision.bidContract.developments.length
          ? bcli.revision.bidContract.developments.map((d) => d.name).join(", ")
          : bcli.revision.bidContract.projects.map((d) => d.name).join(", "),
      w: 1.0,
    }),
    numericColumn<Row>({
      header: "Cost",
      lineItem: (bcli) => priceCell({ valueInCents: bcli.totalCostInCents }),
    }),
    dateColumn<Row>({
      header: "Effective Date",
      lineItem: (bcli) => bcli.revision.startDate.toLocaleDateString(),
    }),
    column<Row>({
      header: "Status",
      lineItem: (bcli) => tagCell(getTagTypeFromStatus(bcli.revision.status.code), bcli.revision.status.name),
    }),
  ];
}
